/**
 * TenantPrivilegesPage — Manage domain privileges and access controls.
 */
import { useMemo, useState, useEffect, type JSX } from 'react'
import { Column } from 'primereact/column'
import { confirmDialog } from 'primereact/confirmdialog'
import {
  Shield, CheckCircle2, AlertTriangle, Key, Plus, Search, RefreshCw,
  Eye, Pencil, Trash2,
} from 'lucide-react'

import RequirePermission from '../../components/auth/RequirePermission.js'
import TenantPrivilegeDrawer from '../../components/platform/TenantPrivilegeDrawer.js'
import DataTable from '../../components/ui/DataTable.js'
import { useTenantPrivileges, type TenantPrivilege } from '../../hooks/useTenantPrivileges.js'
import { useToast } from '../../components/system/ToastProvider.js'
import { formatInTimezone } from '../../utils/timezone.js'

/* ─── Stat Card ─── */
function StatCard({ label, value, icon: Icon, accent }: {
  label: string
  value: number
  icon: typeof Shield
  accent: string
}) {
  return (
    <div className="relative bg-white rounded-sm border border-gray-100 p-5 overflow-hidden group hover:shadow-md hover:border-gray-200 transition-all duration-300">
      <div className="flex items-center gap-4">
        <div
          className="h-11 w-11 rounded-sm flex items-center justify-center shrink-0"
          style={{ background: `${accent}10` }}
        >
          <Icon className="h-5 w-5" style={{ color: accent }} />
        </div>
        <div>
          <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">{label}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1 tracking-tight">{value}</p>
        </div>
      </div>
      <div
        className="absolute bottom-0 left-0 right-0 h-[3px] opacity-0 group-hover:opacity-100 transition-opacity duration-300"
        style={{ background: accent }}
      />
    </div>
  )
}

/* ─── Action Icon ─── */
function ActionIcon({ icon: Icon, label, onClick, color }: {
  icon: typeof Eye
  label: string
  onClick: () => void
  color: string
}) {
  return (
    <button
      onClick={onClick}
      title={label}
      className="h-8 w-8 flex items-center justify-center rounded-full border border-gray-100 text-gray-400 transition-all duration-150 hover:border-transparent hover:text-white"
      style={{ ['--color' as any]: color }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLElement).style.backgroundColor = (e.currentTarget as HTMLElement).style.getPropertyValue('--color')
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent'
        ;(e.currentTarget as HTMLElement).style.borderColor = '#f3f4f6'
      }}
    >
      <Icon className="h-3.5 w-3.5" />
    </button>
  )
}

/* ─── Status Badge ─── */
function StatusBadge({ active }: { active: boolean }) {
  return active ? (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700">
      <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
      Active
    </span>
  ) : (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-500">
      <span className="h-1.5 w-1.5 rounded-full bg-gray-400" />
      Inactive
    </span>
  )
}

/* ─── Permission Pills ─── */
function PermissionPills({ privilege }: { privilege: TenantPrivilege }) {
  const perms = Object.keys(privilege.privilegesDetail || {}).filter(k => privilege.privilegesDetail[k] === true && k !== 'criticalAccess')
  const display = perms.slice(0, 2)
  const remaining = perms.length - 2
  return (
    <div className="flex flex-wrap gap-1.5 items-center">
      {display.map(p => (
        <span key={p} className="inline-flex items-center rounded-sm bg-[#3454cf]/5 px-2 py-0.5 text-[10px] font-semibold text-[#3454cf]">
          {p}
        </span>
      ))}
      {remaining > 0 && (
        <span className="inline-flex items-center rounded-sm bg-gray-100 px-2 py-0.5 text-[10px] font-semibold text-gray-400">
          +{remaining}
        </span>
      )}
    </div>
  )
}

export default function TenantPrivilegesPage(): JSX.Element {
  const toast = useToast()
  const { list, create, update, remove } = useTenantPrivileges()

  const [search, setSearch] = useState('')
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [drawerMode, setDrawerMode] = useState<'create' | 'view' | 'edit'>('create')
  const [selectedPrivilege, setSelectedPrivilege] = useState<TenantPrivilege | undefined>(undefined)
  const [loading, setLoading] = useState(false)
  const [items, setItems] = useState<TenantPrivilege[]>([])
  const [stats, setStats] = useState({ total: 0, active: 0, critical: 0 })

  const fetchPrivileges = async (): Promise<void> => {
    try {
      setLoading(true)
      const response = await list(100, 0)
      setItems(response.tenantPrivileges)
      if (response.stats) setStats(response.stats)
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to fetch tenant privileges')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { void fetchPrivileges() }, [])

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return items
    return items.filter(p => p.name.toLowerCase().includes(q) || (p.description?.toLowerCase().includes(q) ?? false))
  }, [items, search])

  const handleCreate = (): void => {
    setDrawerMode('create')
    setSelectedPrivilege(undefined)
    setDrawerOpen(true)
  }

  const handleEdit = (privilege: TenantPrivilege): void => {
    setDrawerMode('edit')
    setSelectedPrivilege(privilege)
    setDrawerOpen(true)
  }

  const handleView = (privilege: TenantPrivilege): void => {
    setDrawerMode('view')
    setSelectedPrivilege(privilege)
    setDrawerOpen(true)
  }

  const handleDelete = (privilege: TenantPrivilege): void => {
    confirmDialog({
      message: `Delete "${privilege.name}"? This action cannot be undone.`,
      header: `Delete ${privilege.name}`,
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      accept: async () => {
        try {
          await remove(privilege.id)
          toast.success(`${privilege.name} deleted`)
          await fetchPrivileges()
        } catch (e) {
          toast.error(e instanceof Error ? e.message : 'Failed to delete tenant privilege')
        }
      },
    })
  }

  const handleSave = async (data: {
    status: boolean
    name: string
    description: string
    privilegesDetail: Record<string, unknown>
  }): Promise<void> => {
    try {
      setLoading(true)
      if (drawerMode === 'edit' && selectedPrivilege) {
        await update(selectedPrivilege.id, data)
        toast.success(`${data.name} updated`)
      } else {
        await create(data)
        toast.success(`${data.name} created`)
      }
      setDrawerOpen(false)
      await fetchPrivileges()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to save tenant privilege')
    } finally {
      setLoading(false)
    }
  }

  return (
    <RequirePermission permission="platform:tenants_manage">
      <div className="w-full space-y-6 px-4 pb-10">
        {/* Page Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Tenant Privileges</h1>
            <p className="text-sm text-gray-400 mt-1">Manage domain privileges and access controls.</p>
          </div>
          <button
            onClick={handleCreate}
            className="inline-flex items-center gap-2 bg-[#3454cf] hover:bg-[#2a45b0] text-white text-sm font-semibold px-5 py-2.5 rounded-sm transition-all shadow-md"
          >
            <Plus className="h-4 w-4" />
            Add Privilege
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          <StatCard label="Total Privileges" value={stats.total} icon={Shield} accent="#3454cf" />
          <StatCard label="Active Privileges" value={stats.active} icon={CheckCircle2} accent="#10b981" />
          <StatCard label="Critical Gateway" value={stats.critical} icon={AlertTriangle} accent="#f6931c" />
          <StatCard label="Total Permissions" value={148} icon={Key} accent="#8b5cf6" />
        </div>

        {/* Toolbar */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="relative w-80">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-300" />
            <input
              type="text"
              placeholder="Search privileges by name or description..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full h-11 pl-10 pr-4 text-sm bg-white border border-gray-200 rounded-sm text-gray-700 placeholder:text-gray-300 focus:border-[#f6931c] focus:ring-[3px] focus:ring-[#f6931c]/10 focus:outline-none transition-all"
            />
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-400 font-medium">{visible.length} privileges</span>
            <div className="w-px h-4 bg-gray-200" />
            <button
              onClick={() => { setSearch(''); void fetchPrivileges() }}
              className="h-9 w-9 flex items-center justify-center rounded-sm text-gray-400 hover:text-[#3454cf] hover:bg-[#3454cf]/5 transition-all border border-gray-200 hover:border-[#3454cf]/20"
              title="Refresh"
            >
              <RefreshCw className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Table */}
        <DataTable
          value={visible}
          loading={loading}
          paginator
          rows={10}
          rowsPerPageOptions={[10, 25, 50]}
          className="shadow-[0_2px_12px_rgba(0,0,0,0.04)] rounded-sm border-gray-100"
          emptyMessage="No privileges found."
        >
          <Column
            field="name"
            header="Privilege Name"
            sortable
            body={(privilege: TenantPrivilege) => {
              const shortId = privilege.id.split('-')[0]?.substring(0, 4) || '??'
              return (
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-sm bg-[#3454cf]/5 flex items-center justify-center shrink-0">
                    <Shield className="h-5 w-5 text-[#3454cf]" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">{privilege.name}</p>
                    <p className="text-xs text-gray-300">ID: {shortId}</p>
                  </div>
                </div>
              )
            }}
            style={{ width: '25%' }}
          />
          <Column
            header="Permissions"
            body={(privilege: TenantPrivilege) => <PermissionPills privilege={privilege} />}
          />
          <Column
            field="status"
            header="Status"
            sortable
            body={(privilege: TenantPrivilege) => <StatusBadge active={privilege.status} />}
          />
          <Column
            field="createdAt"
            header="Created"
            sortable
            body={(privilege: TenantPrivilege) => (
              <span className="text-xs text-gray-400">
                {formatInTimezone(privilege.createdAt, { month: 'short', day: 'numeric', year: 'numeric' }, 'en-US')}
              </span>
            )}
          />
          <Column
            header="Actions"
            body={(privilege: TenantPrivilege) => (
              <div className="flex items-center gap-1">
                <ActionIcon icon={Eye} label="View" onClick={() => handleView(privilege)} color="#3454cf" />
                <ActionIcon icon={Pencil} label="Edit" onClick={() => handleEdit(privilege)} color="#3454cf" />
                <ActionIcon icon={Trash2} label="Delete" onClick={() => handleDelete(privilege)} color="#ef4444" />
              </div>
            )}
            style={{ width: '120px' }}
          />
        </DataTable>
      </div>

      <TenantPrivilegeDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        mode={drawerMode}
        privilege={selectedPrivilege}
        onSave={handleSave}
        loading={loading}
      />
    </RequirePermission>
  )
}
